import React, { useRef, useState } from 'react';
import { Upload, X, Link as LinkIcon, Plus, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { compressImage, formatBytes } from '@/lib/image-utils';

interface AdminMultiImageUploadProps {
  value: string[];
  onChange: (images: string[]) => void;
  label?: string;
}

const AdminMultiImageUpload: React.FC<AdminMultiImageUploadProps> = ({
  value,
  onChange,
  label = 'Gallery Images',
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [mode, setMode] = useState<'upload' | 'url'>('upload');
  const [dragOver, setDragOver] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [compressing, setCompressing] = useState(false);

  const handleFileSelect = async (file: File): Promise<void> => {
    if (!file.type.startsWith('image/')) {
      toast.error(`"${file.name}" is not an image file`);
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.error(`"${file.name}" exceeds 50MB limit (${formatBytes(file.size)})`);
      return;
    }

    setCompressing(true);
    try {
      const { dataUrl, originalSize, compressedSize } = await compressImage(file);
      onChange([...value, dataUrl]);
      toast.success(`Compressed: ${formatBytes(originalSize)} → ${formatBytes(compressedSize)}`);
    } catch {
      toast.error(`Failed to process "${file.name}"`);
    } finally {
      setCompressing(false);
    }
  };

  /** Compress all files in parallel, then add all results at once */
  const handleMultipleFiles = async (files: File[]): Promise<void> => {
    const validFiles = files.filter((f) => {
      if (!f.type.startsWith('image/')) { toast.error(`"${f.name}" is not an image file`); return false; }
      if (f.size > 50 * 1024 * 1024) { toast.error(`"${f.name}" exceeds 50MB limit`); return false; }
      return true;
    });
    if (validFiles.length === 0) return;

    setCompressing(true);
    const results: string[] = [];
    let totalOriginal = 0;
    let totalCompressed = 0;

    try {
      const promises = validFiles.map(async (file) => {
        try {
          const { dataUrl, originalSize, compressedSize } = await compressImage(file);
          totalOriginal += originalSize;
          totalCompressed += compressedSize;
          return dataUrl;
        } catch {
          toast.error(`Failed to process "${file.name}"`);
          return null;
        }
      });

      const settled = await Promise.all(promises);
      for (const r of settled) {
        if (r) results.push(r);
      }

      if (results.length > 0) {
        onChange([...value, ...results]);
        toast.success(`${results.length} images compressed: ${formatBytes(totalOriginal)} → ${formatBytes(totalCompressed)}`);
      }
    } finally {
      setCompressing(false);
    }
  };

  const handleDrop = (e: React.DragEvent): void => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length === 1 && files[0]) {
      void handleFileSelect(files[0]);
    } else if (files.length > 1) {
      void handleMultipleFiles(files);
    }
  };

  const handleDragOver = (e: React.DragEvent): void => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleRemove = (index: number): void => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handleAddUrl = (): void => {
    const url = urlInput.trim();
    if (!url) return;
    onChange([...value, url]);
    setUrlInput('');
  };

  const handleUrlKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddUrl();
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-[9px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40">
          {label}
        </label>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setMode('upload')}
            className={`flex items-center gap-1 px-2 py-1 text-[8px] font-bold uppercase tracking-wider rounded-md transition-all ${
              mode === 'upload'
                ? 'bg-black dark:bg-white text-white dark:text-black'
                : 'text-black/30 dark:text-white/30 hover:text-black/60 dark:hover:text-white/60'
            }`}
          >
            <Upload size={9} /> Upload
          </button>
          <button
            type="button"
            onClick={() => setMode('url')}
            className={`flex items-center gap-1 px-2 py-1 text-[8px] font-bold uppercase tracking-wider rounded-md transition-all ${
              mode === 'url'
                ? 'bg-black dark:bg-white text-white dark:text-black'
                : 'text-black/30 dark:text-white/30 hover:text-black/60 dark:hover:text-white/60'
            }`}
          >
            <LinkIcon size={9} /> URL
          </button>
        </div>
      </div>

      {mode === 'url' ? (
        <div className="flex gap-2">
          <input
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            onKeyDown={handleUrlKeyDown}
            placeholder="https://example.com/image.jpg"
            className="flex-1 px-4 py-3 bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-xl text-sm font-medium outline-none focus:border-black/20 dark:focus:border-white/20 transition-all placeholder:text-black/20 dark:placeholder:text-white/20"
          />
          <button
            type="button"
            onClick={handleAddUrl}
            className="px-4 py-3 bg-black dark:bg-white text-white dark:text-black rounded-xl text-[10px] font-bold uppercase tracking-wider hover:opacity-90 transition-opacity"
          >
            <Plus size={14} />
          </button>
        </div>
      ) : (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={() => setDragOver(false)}
          onClick={() => !compressing && fileInputRef.current?.click()}
          className={`w-full h-24 border-2 border-dashed rounded-xl cursor-pointer flex flex-col items-center justify-center gap-1.5 transition-all ${
            compressing
              ? 'border-black/20 dark:border-white/20 bg-black/[0.04] dark:bg-white/[0.06]'
              : dragOver
                ? 'border-black/30 dark:border-white/30 bg-black/[0.06] dark:bg-white/[0.08]'
                : 'border-black/[0.08] dark:border-white/[0.08] bg-black/[0.02] dark:bg-white/[0.02] hover:border-black/15 dark:hover:border-white/15 hover:bg-black/[0.04] dark:hover:bg-white/[0.04]'
          }`}
        >
          {compressing ? (
            <>
              <Loader2 size={16} className="text-black/30 dark:text-white/30 animate-spin" />
              <span className="text-[9px] font-bold uppercase tracking-wider text-black/30 dark:text-white/30">
                Compressing...
              </span>
            </>
          ) : (
            <>
              <Upload size={16} className="text-black/20 dark:text-white/20" />
              <span className="text-[9px] font-bold uppercase tracking-wider text-black/25 dark:text-white/25">
                Drop images or click to add
              </span>
              <span className="text-[8px] text-black/15 dark:text-white/15">
                Any format — auto-compressed
              </span>
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              if (files.length === 1 && files[0]) {
                void handleFileSelect(files[0]);
              } else if (files.length > 1) {
                void handleMultipleFiles(files);
              }
              e.target.value = '';
            }}
            className="hidden"
          />
        </div>
      )}

      {/* Image grid preview */}
      {value.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-3">
          {value.map((img, i) => (
            <div key={i} className="relative group aspect-square rounded-xl overflow-hidden border border-black/[0.06] dark:border-white/[0.08]">
              <img
                src={img}
                alt={`Gallery ${i + 1}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
              <button
                type="button"
                onClick={() => handleRemove(i)}
                className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-sm opacity-0 group-hover:opacity-100"
              >
                <X size={10} />
              </button>
              <span className="absolute bottom-1 left-1 text-[8px] font-bold text-white/60 bg-black/40 px-1.5 py-0.5 rounded">
                {i + 1}
              </span>
            </div>
          ))}
        </div>
      )}

      {value.length > 0 && (
        <p className="text-[9px] text-black/25 dark:text-white/25 mt-2">
          {value.length} image{value.length !== 1 ? 's' : ''} added
        </p>
      )}
    </div>
  );
};

export default AdminMultiImageUpload;
